import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Lock, Unlock, Trash2, KeyRound, PauseCircle, PlayCircle, ShieldOff } from "lucide-react";
import { useMemo } from "react";

const formatDate = (iso) => {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Subscription meter — visualizes remaining days until subscription end.
const SubscriptionMeter = ({ sub }) => {
  const meta = useMemo(() => {
    if (!sub || !sub.start_date || !sub.end_date) return null;
    const startMs = new Date(sub.start_date).getTime();
    const endMs = new Date(sub.end_date).getTime();
    const nowMs = Date.now();
    const totalMs = endMs - startMs;
    const elapsedMs = nowMs - startMs;
    const remainingMs = endMs - nowMs;
    const pct = totalMs > 0 ? Math.max(0, Math.min(100, (elapsedMs / totalMs) * 100)) : 100;
    const daysLeft = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
    return { pct, daysLeft, remainingMs };
  }, [sub]);

  if (!meta) return <span className="text-xs font-mono text-muted-foreground">—</span>;

  const { pct, daysLeft, remainingMs } = meta;
  const expired = remainingMs <= 0;
  const warning = !expired && daysLeft <= 7;
  const soon = !expired && !warning && daysLeft <= 30;

  const barColor = expired
    ? "bg-red-500"
    : warning
    ? "bg-red-400"
    : soon
    ? "bg-yellow-400"
    : "bg-green-500";
  const label = expired
    ? `Expired ${Math.abs(daysLeft)}d ago`
    : daysLeft === 0
    ? "Ends today"
    : `${daysLeft}d left`;

  return (
    <div className="min-w-[110px]" title={`${formatDate(sub.start_date)} → ${formatDate(sub.end_date)}`}>
      <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
        <span className="text-muted-foreground">{sub.plan || "—"}</span>
        <span className={expired ? "text-red-500" : warning ? "text-red-400" : soon ? "text-yellow-500" : "text-green-500"}>
          {label}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-muted/40 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const UsersTab = ({
  users,
  onToggleActive,
  onResetPassword,
  onResetPin,
  onDelete,
  onPause,
  onUnpause,
  onEdit,
}) => {
  // Paused (awaiting activation) users float to the top so approvals are never missed
  const sortedUsers = [...users].sort((a, b) => (b.is_paused === true) - (a.is_paused === true));
  const pendingCount = users.filter((u) => u.is_paused).length;
  return (
    <Card className="border-border/50">
      <CardHeader className="py-3">
        <CardTitle className="font-mono text-base flex items-center gap-2">
          User Management
          <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-xs font-bold" data-testid="users-total-count">
            {users.length} {users.length === 1 ? "user" : "users"}
          </span>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500 text-xs font-bold" data-testid="users-pending-count">
              {pendingCount} awaiting activation
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-muted/30 text-xs font-mono">
                <th className="p-2 text-right w-8">#</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Email</th>
                <th className="p-2 text-center">Role</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Data Entry</th>
                <th className="p-2 text-center">Entries</th>
                <th className="p-2 text-center">Subscription</th>
                <th className="p-2 text-left">Signed Up</th>
                <th className="p-2 text-left">Last Login</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((u, idx) => (
                <tr key={u.id} className={`border-t border-border/30 text-sm ${u.is_paused ? 'bg-yellow-500/5' : ''}`}>
                  <td className="p-2 text-right font-mono text-xs text-muted-foreground tabular-nums" data-testid={`user-row-serial-${u.id}`}>
                    {idx + 1}
                  </td>
                  <td
                    className="p-2 font-mono cursor-pointer hover:text-primary"
                    onClick={() => onEdit(u)}
                    data-testid={`user-row-name-${u.id}`}
                  >
                    <div className="underline decoration-dotted">{u.name}</div>
                    {u.store_name && (
                      <div className="text-xs text-muted-foreground">{u.store_name}</div>
                    )}
                  </td>
                  <td className="p-2 text-muted-foreground">
                    <div>{u.email}</div>
                    {u.store_address && (
                      <div className="text-xs text-muted-foreground/70">{u.store_address}</div>
                    )}
                  </td>
                  <td className="p-2 text-center">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-mono ${
                        u.role === 'admin'
                          ? 'bg-primary/20 text-primary'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {u.role || "user"}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-mono ${
                        u.is_active !== false
                          ? 'bg-green-500/20 text-green-500'
                          : 'bg-red-500/20 text-red-500'
                      }`}
                    >
                      {u.is_active !== false ? "Active" : "Locked"}
                    </span>
                  </td>
                  <td className="p-2 text-center">
                    {u.is_paused ? (
                      <span
                        className="px-2 py-0.5 rounded text-xs font-mono bg-yellow-500/20 text-yellow-500"
                        data-testid={`pause-status-${u.id}`}
                      >
                        Paused
                      </span>
                    ) : (
                      <span
                        className="px-2 py-0.5 rounded text-xs font-mono bg-green-500/20 text-green-500"
                        data-testid={`pause-status-${u.id}`}
                      >
                        Active
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-center font-mono">{u.entry_count || 0}</td>
                  <td className="p-2" data-testid={`sub-meter-${u.id}`}>
                    <SubscriptionMeter sub={u.subscription} />
                  </td>
                  <td className="p-2 text-xs text-muted-foreground">{formatDate(u.created_at)}</td>
                  <td className="p-2 text-xs text-muted-foreground">{formatDate(u.last_login)}</td>
                  <td className="p-2 text-center">
                    {u.role !== "admin" && (
                      <div className="flex gap-1 justify-center flex-wrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onToggleActive(u.id)}
                          className="h-7 px-2 gap-1"
                          data-testid={`toggle-${u.id}`}
                        >
                          {u.is_active !== false ? (
                            <>
                              <Lock className="w-3.5 h-3.5 text-red-500" />
                              <span className="text-xs text-red-500">Lock</span>
                            </>
                          ) : (
                            <>
                              <Unlock className="w-3.5 h-3.5 text-green-500" />
                              <span className="text-xs text-green-500">Unlock</span>
                            </>
                          )}
                        </Button>
                        {u.is_paused ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onUnpause(u)}
                            className="h-7 px-2 gap-1"
                            data-testid={`unpause-${u.id}`}
                          >
                            <PlayCircle className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs text-green-500">Activate</span>
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onPause(u)}
                            className="h-7 px-2 gap-1"
                            data-testid={`pause-${u.id}`}
                          >
                            <PauseCircle className="w-3.5 h-3.5 text-yellow-500" />
                            <span className="text-xs text-yellow-500">Pause</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onResetPassword(u)}
                          className="h-7 px-2 gap-1"
                          data-testid={`reset-${u.id}`}
                        >
                          <KeyRound className="w-3.5 h-3.5 text-blue-500" />
                          <span className="text-xs text-blue-500">Reset</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onResetPin(u)}
                          className="h-7 px-2 gap-1"
                          title="Clear this user's 4-digit Settings PIN"
                          data-testid={`reset-pin-${u.id}`}
                        >
                          <ShieldOff className="w-3.5 h-3.5 text-orange-500" />
                          <span className="text-xs text-orange-500">PIN</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDelete(u)}
                          disabled={u.is_active !== false}
                          className="h-7 px-2 gap-1"
                          title={u.is_active !== false ? "Lock user first to enable delete" : "Delete user"}
                          data-testid={`delete-${u.id}`}
                        >
                          <Trash2
                            className={`w-3.5 h-3.5 ${
                              u.is_active !== false ? 'text-muted-foreground/30' : 'text-red-500'
                            }`}
                          />
                          <span
                            className={`text-xs ${
                              u.is_active !== false ? 'text-muted-foreground/30' : 'text-red-500'
                            }`}
                          >
                            Delete
                          </span>
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
